import React from 'react';
import firebase from '../../utilities/firebase';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, useTheme } from '@mui/material/styles';

import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { useDatePickerSlotProps } from '../../utilities/ModalStackContext';
import { StateManager } from '../../utilities/stateManager';

// Every date the title packet prints, where each one came from, and a way to
// set it by hand. The server works the dates out from the records
// (dmv/packetDates.js); a date set here is stored on
// titles/{stock}.date_overrides and wins until it is cleared.

// Where a date came from, in words.
const SOURCE = {
  signed_session: 'the day the sales packet was e-signed',
  car_record: 'the day the sales packet was signed',
  day_after_transfer: 'the day after the last title transfer',
  deal_date: 'the deal date — no signed sales packet',
  car_date: 'the day Skyway took the car — no sale yet',
  final_deposit: 'the final deposit',
  sale_date: 'the sale date',
  contract_start: 'the consignment start',
  consignment_signed: 'the day the consignment was signed',
  clerk: 'set by hand',
  actual_miles: 'actual miles',
  records: 'the records',
};

// What the cutter did with the date already on a POA.
const POA_ACTION = {
  kept: 'kept the date on the POA',
  filled: 'filled a blank date',
  corrected: 'replaced a date after the sale',
  override: 'used the date set by hand',
  missing: 'no date to fill',
  no_field: 'date boxes not found on the page',
};

const show = (day) => (day ? moment(day, 'YYYY-MM-DD').format('MM/DD/YYYY') : '—');

function DateOverride({ value, onChange, disabled }) {
  const slots = useDatePickerSlotProps();
  const parsed = value ? moment(value, 'YYYY-MM-DD', true) : null;
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DatePicker
        label="Set by hand"
        value={parsed && parsed.isValid() ? parsed : null}
        onChange={(next) => next && next.isValid() && onChange(next.format('YYYY-MM-DD'))}
        disabled={disabled}
        slotProps={{
          textField: { size: 'small', sx: { width: 170 } },
          popper: slots.popper,
          dialog: slots.dialog,
        }}
      />
    </LocalizationProvider>
  );
}

function DateRow({ label, hint, slot, poa, onSet, onClear }) {
  const byHand = slot.source === 'clerk';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 0.75,
               borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ width: 150 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        {hint && <Typography variant="caption" sx={{ color: 'text.disabled' }}>{hint}</Typography>}
      </Box>
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{show(slot.value)}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {slot.value ? `From ${SOURCE[slot.source] || slot.source}` : 'Could not be worked out'}
          {byHand && slot.computed ? `. The records give ${show(slot.computed)}.` : ''}
          {slot.pending ? ' — in the packet after the next build' : ''}
        </Typography>
        {poa && poa.poa_date_source && (
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
            In the packet: {show(poa.poa_date)} ({POA_ACTION[poa.poa_date_source] || poa.poa_date_source})
            {poa.stale ? ' — updates on the next build' : ''}
          </Typography>
        )}
      </Box>
      <DateOverride value={byHand ? slot.value : null} onChange={onSet} />
      {byHand && <Button size="small" onClick={onClear}>Use the records</Button>}
    </Box>
  );
}

// A date set here shows at once and saves at once. The packet rebuild is the
// slow part, so it waits until the edits stop and then runs one time.
const REBUILD_DELAY_MS = 1500;

// The slot as it will read once the server catches up with a pending edit.
function withPending(slot, pending) {
  if (pending === undefined) return slot;
  if (pending) {
    return { ...slot, value: pending, source: 'clerk', pending: true,
             computed: slot.source === 'clerk' ? slot.computed : slot.value };
  }
  if (slot.source !== 'clerk') return slot;
  return { ...slot, value: slot.computed, source: 'records', pending: true };
}

export default function PacketDates({ stockNumber, dates, findings = [], requirements = [], isConsignment, onChanged }) {
  const theme = useTheme();
  const [pending, setPending] = React.useState({});
  const [saving, setSaving] = React.useState(0);
  const [rebuilding, setRebuilding] = React.useState(false);
  const [appointee, setAppointee] = React.useState('');

  const onChangedRef = React.useRef(onChanged);
  onChangedRef.current = onChanged;
  const timer = React.useRef(null);
  const running = React.useRef(false);
  const again = React.useRef(false);
  const mounted = React.useRef(true);

  const rebuild = React.useCallback(async () => {
    timer.current = null;
    if (running.current) { again.current = true; return; }
    running.current = true;
    if (mounted.current) setRebuilding(true);
    try {
      await onChangedRef.current?.();
    } finally {
      running.current = false;
      if (mounted.current) setRebuilding(false);
    }
    if (again.current) { again.current = false; rebuild(); }
  }, []);

  const scheduleRebuild = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(rebuild, REBUILD_DELAY_MS);
  };

  // Closing the car before the delay runs out still rebuilds the packet.
  React.useEffect(() => () => {
    mounted.current = false;
    if (timer.current) { clearTimeout(timer.current); rebuild(); }
  }, [rebuild]);

  // Drop each pending edit once the server's dates show it.
  React.useEffect(() => {
    if (!dates) return;
    setPending(prev => {
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        const slot = dates[key] || {};
        const landed = value ? slot.source === 'clerk' && slot.value === value : slot.source !== 'clerk';
        if (!landed) next[key] = value;
      });
      return next;
    });
  }, [dates]);

  const appointeeSlot = (dates && dates.appointee) || {};
  React.useEffect(() => {
    setAppointee(appointeeSlot.source === 'clerk' ? appointeeSlot.value || '' : '');
  }, [appointeeSlot.source, appointeeSlot.value]);

  if (!dates) return null;

  const write = async (patch) => {
    setSaving(n => n + 1);
    try {
      await firebase.firestore().doc('titles/' + stockNumber).set(patch, { merge: true });
      scheduleRebuild();
      return true;
    } catch (e) {
      StateManager.setAlertAndOpen('Could not save: ' + e.message, 'error');
      return false;
    } finally {
      setSaving(n => n - 1);
    }
  };

  const setDate = async (key, value) => {
    setPending(prev => ({ ...prev, [key]: value || null }));
    const ok = await write({
      date_overrides: { [key]: value || firebase.firestore.FieldValue.delete() },
    });
    if (!ok) {
      setPending(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const saveAppointee = () => write({
    appointee_override: appointee.trim() || firebase.firestore.FieldValue.delete(),
  });

  const req = (key) => requirements.find(r => r.key === key) || null;
  const buyerPoa = req('buyer_poa');
  const consignorPoa = req('consignor_poa');
  const inputs = dates.inputs || {};
  const problems = findings.filter(f => f.level === 'warning');
  const notes = findings.filter(f => f.level !== 'warning');

  const rows = [
    { key: 'sale_date', label: 'Sale date', hint: 'Sections 4, 7 and 12' },
    { key: 'date_acquired', label: 'Date acquired', hint: 'Section 5' },
    { key: 'application_date', label: 'Signing date', hint: 'Section 8' },
  ];
  if (buyerPoa) rows.push({ key: 'buyer_poa_date', label: 'Buyer POA date', hint: 'On or before the sale', poa: buyerPoa });
  if (isConsignment || consignorPoa) {
    rows.push({ key: 'consignor_poa_date', label: 'Consignor POA date', hint: 'Blank uses the consignment start', poa: consignorPoa });
  }

  return (
    <Box sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid',
               borderColor: problems.length ? alpha(theme.palette.warning.main, 0.5) : 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
          Packet dates
        </Typography>
        {(saving > 0 || rebuilding) && (
          <>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {saving > 0 ? 'Saving…' : 'Rebuilding the packet…'}
            </Typography>
            <CircularProgress size={14} />
          </>
        )}
      </Box>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mb: 0.5 }}>
        Signed {show(inputs.sales_signed)} · last transfer {show(inputs.last_transfer)} · final deposit {show(inputs.final_deposit)}
        {isConsignment ? ` · consignment start ${show(inputs.contract_start)}` : ''}
      </Typography>

      {problems.length > 0 && (
        <Box sx={{ p: 1, my: 0.75, borderRadius: 1, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
          {problems.map((f, i) => (
            <Typography key={i} variant="caption" sx={{ display: 'block', color: 'warning.dark' }}>{f.message}</Typography>
          ))}
        </Box>
      )}

      <Stack>
        {rows.map(r => (
          <DateRow
            key={r.key}
            label={r.label}
            hint={r.hint}
            slot={withPending(dates[r.key] || {}, pending[r.key])}
            poa={r.poa}
            onSet={(v) => setDate(r.key, v)}
            onClear={() => setDate(r.key, null)}
          />
        ))}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 0.75 }}>
          <Box sx={{ width: 150 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>POA appointee</Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>Both POAs</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {appointeeSlot.value || 'Skyway Classics (as on the document)'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {appointeeSlot.source === 'clerk' ? 'Set by hand'
                : appointeeSlot.source === 'actual_miles' ? 'Actual miles: Skyway Classics and Ryan Tanski'
                : 'Not actual miles'}
            </Typography>
          </Box>
          <TextField
            size="small" label="Set by hand" value={appointee}
            onChange={(e) => setAppointee(e.target.value)}
            sx={{ width: 240 }}
          />
          <Button size="small" disabled={saving > 0 || appointee.trim() === (appointeeSlot.source === 'clerk' ? appointeeSlot.value : '')}
            onClick={saveAppointee}>
            {appointee.trim() ? 'Save' : 'Use the rule'}
          </Button>
        </Box>
      </Stack>

      {notes.length > 0 && (
        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
          {notes.map((f, i) => (
            <Typography key={i} variant="caption" sx={{ color: 'text.disabled' }}>{f.message}</Typography>
          ))}
        </Stack>
      )}
    </Box>
  );
}
